import React, { type ReactElement } from 'react';
import clsx from 'clsx';
import ExternalLink from 'atom-components/external-link';
import Heading from 'atom-components/heading';
import TooltipItem from 'atom-components/tooltip-item';
import MarkStatusBadge from 'feature-components/badges/mark-status-badge';
import { fileDownloadHref } from 'helpers/file-download.helpers';
import { type AuthorisableMark } from 'policies/entities/mark';
import {
  type BaseMediaObject,
  MediaObjectType
} from 'services/assist-api/entities/media-object/types';
import type { MarkStatus } from 'services/assist-api/entities/test-instance-response-mark/types';
import type { TestInstanceResponseMarkerWithCustomerUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import ActiveMarkerBadge from './active-marker-badge';
import styles from './info-section.module.scss';

type MediaObjectProps = {
  mediaObject: BaseMediaObject;
};

function MediaObject({ mediaObject }: MediaObjectProps): ReactElement {
  function displayMediaType(mediaType: MediaObjectType): string {
    switch (mediaType) {
      case MediaObjectType.TestRubric:
        return 'rubric';
      case MediaObjectType.TestInstanceResponse:
        return 'response';
      default:
        return 'ERROR: Unknown type';
    }
  }

  return (
    <div className={styles.block}>
      <TooltipItem
        tooltip={`View PDF for this ${displayMediaType(mediaObject.type)} in a new tab.`}
      >
        <ExternalLink
          href={fileDownloadHref(mediaObject.contentUrl)}
          className={clsx(
            styles.mediaObjectLink,
            {
              [`${styles.testRubricMediaObjectLink}`]:
                mediaObject.type === MediaObjectType.TestRubric
            },
            {
              [`${styles.testInstanceResponseMediaObjectLink}`]:
                mediaObject.type === MediaObjectType.TestInstanceResponse
            }
          )}
        >
          {mediaObject.displayName}
        </ExternalLink>
      </TooltipItem>
    </div>
  );
}

type InfoSectionProps = {
  authorisableMark: AuthorisableMark;
  isActiveMarker: boolean;
  activeMarker: TestInstanceResponseMarkerWithCustomerUser | undefined;
  rubricMediaObjects: BaseMediaObject[];
  responseMediaObjects: BaseMediaObject[];
  markStatus: MarkStatus;
};

export default function InfoSection({
  authorisableMark,
  isActiveMarker,
  activeMarker,
  rubricMediaObjects,
  responseMediaObjects,
  markStatus
}: InfoSectionProps): ReactElement {
  function renderSortedMediaObjects(mediaObjects: BaseMediaObject[]): ReactElement[] {
    return mediaObjects
      .sort((a, b) => a.order - b.order) // Todo: Convert to toSorted after Node 20 upgrade
      .map((mediaObject) => <MediaObject key={mediaObject.id} mediaObject={mediaObject} />);
  }

  return (
    <div className={styles.infoSection}>
      <Heading level={2}>Mark Response</Heading>
      <div className={styles.blocks}>
        <div className={styles.block}>
          <MarkStatusBadge type={authorisableMark.type}>{markStatus}</MarkStatusBadge>
        </div>
        <div className={styles.block}>
          <ActiveMarkerBadge
            authorisableMark={authorisableMark}
            isActiveMarker={isActiveMarker}
            activeMarker={activeMarker}
          />
        </div>
        {renderSortedMediaObjects(rubricMediaObjects)}
        {renderSortedMediaObjects(responseMediaObjects)}
      </div>
    </div>
  );
}
